import {
  Box,
  Typography,
  Button,
  Fab,
  Paper,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import NavigationIcon from "@mui/icons-material/Navigation";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useEffect, useRef } from "react";
import Collapse from "@mui/material/Collapse";
import Fade from "@mui/material/Fade";

const SectionNavigation = ({
  company,
  sections,
  onNavigate,
  toggle,
  openNavList,
}) => {
  const theme = useTheme();
  const navRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navRef.current &&
        !navRef.current.contains(event.target) &&
        !event.target.closest(".MuiFab-root")
      ) {
        toggle();
      }
    };

    if (openNavList) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openNavList, toggle]);

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: company.companyType !== "investment" ? "60px" : "180px",
          right: 0,
          zIndex: 1100,
          height: "calc(100vh - 60px)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Collapse
          in={openNavList}
          timeout={350}
          orientation='horizontal'
          sx={{
            width: openNavList ? "400px" : 0,
            minWidth: 0,
            maxWidth: "400px",
            transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Fade in={openNavList} timeout={350}>
            <Paper
              ref={navRef}
              elevation={0}
              sx={{
                position: "relative",
                backgroundColor: "background.paper",
                padding: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                maxHeight: "calc(100vh - 100px)",
                overflowY: "auto",
                width: "400px",
                minWidth: "400px",
                maxWidth: "400px",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={2}
                  sx={{
                    pb: 2,
                    borderBottom: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.1),
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    }}
                  >
                    <ReceiptLongIcon color='primary' />
                  </Box>
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Sections
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  {Object.entries(sections).map(([section, items]) => (
                    <Box key={section}>
                      <Typography
                        variant='subtitle2'
                        sx={{
                          color: "text.secondary",
                          mb: 1,
                          pl: 1,
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          fontSize: "0.75rem",
                        }}
                      >
                        Section {section}
                      </Typography>
                      <Stack spacing={0.5}>
                        {items.map((item) => (
                          <Button
                            key={item.id}
                            onClick={() => {
                              onNavigate(item);
                              toggle(); // Close navigation after clicking
                            }}
                            sx={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              justifyContent: "flex-start",
                              py: 1.5,
                              px: 2,
                              borderRadius: 2,
                              color: "text.primary",
                              textTransform: "none",
                              fontWeight: 500,
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.08
                                ),
                              },
                              "&:active": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.12
                                ),
                              },
                            }}
                          >
                            {item.label}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Fade>
        </Collapse>
      </Box>

      <Fab
        color='primary'
        aria-label='navigation'
        className='MuiFab-root'
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1101,
          transform: openNavList ? "rotate(0deg)" : "rotate(180deg)",
          transition: "all 0.3s ease-in-out",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
        }}
        onClick={toggle}
      >
        <NavigationIcon />
      </Fab>
    </>
  );
};

export default SectionNavigation;
